import { createContext, useCallback, useContext } from "react";

/**
 * Where the shared list toolbar's "create" button should navigate.
 *
 * WHY THIS EXISTS
 * ---------------
 * `AdvancedSearchToolbar` is the default toolbar for the shared `DataGrid`, and
 * its Create FAB used to do a bare `navigate("create")`. React Router v6
 * resolves a relative navigate against the matched ROUTE, not against the URL,
 * so what that produces depends entirely on where the CONSUMER mounted its
 * listing:
 *
 *   listing at `index` or `path="*"`   ->  <moduleBase>/create      correct
 *   listing at `path="list"`           ->  <moduleBase>/list/create no route
 *
 * The overwhelming majority of consumers are the first shape, so the bare form
 * is right for them and must not change. A shared component cannot know which
 * shape it is in — so the route group that INTRODUCES the extra segment (a
 * listing-mode layout: Address Book's overview/list, Opportunities'
 * list/pipeline/dashboard) declares its own base here, and the toolbar resolves
 * an ABSOLUTE path against it instead of guessing a hop count.
 *
 * Declaring it at the layout — rather than as a prop on each grid — puts the
 * declaration at the CAUSE. One layout covers every mode under it, so adding a
 * third mode to Address Book cannot reintroduce the bug.
 *
 * `src/components/datagrid/__tests__/ListingModeCreateNav.test.tsx` enforces
 * this: it scans the tree for modules whose mode routes render the shared
 * toolbar with create enabled and fails if the module has not declared a base.
 * A module that gains a mode route later goes red in CI rather than shipping a
 * blank page.
 */
export interface ListingRouteBase {
  /**
   * Absolute pathname of the module base — the level the module's `create` /
   * `:id` / `import` routes are declared at, e.g. "/AddressBook". Derive it
   * from the live pathname; never hardcode it. App.tsx mounts modules at
   * `menuItem.to + "/*"` straight from the backend nav, so the same module is
   * "/AddressBook" on one tenant and something else on another.
   *
   * OMIT IT when the listing is already at the module base and the only thing
   * that needs correcting is the create route's NAME. A base-mounted listing
   * resolves a relative segment correctly on its own, so declaring a base there
   * would be noise — and getting it wrong would be worse than leaving it out.
   */
  base?: string;
  /**
   * Name of the module's create route. Defaults to "create". Declare it when the
   * module named that route something else — Opportunities, flight_schedule,
   * aircraft_roster and metabase all declare `new` — so the FAB targets the
   * route that exists rather than a name it never had. Prefer this to renaming
   * the route, which breaks any URL a user has bookmarked.
   */
  createSegment?: string;
}

const ListingRouteBaseContext = createContext<ListingRouteBase | null>(null);

export const ListingRouteBaseProvider = ListingRouteBaseContext.Provider;

/**
 * Resolve the create target for a shared list toolbar.
 *
 * Three cases, in the order they are decided:
 *
 *  1. Nothing declared -> relative "create". Every consumer whose listing sits
 *     at the module base, and byte-identical to the behaviour they had before
 *     this context existed.
 *  2. A createSegment but no base -> that segment, still RELATIVE. For a
 *     base-mounted listing this already resolves to <base>/<segment>, so it
 *     fixes a mis-named create route without anyone having to state a base.
 *  3. A base -> an absolute "<base>/<segment>", which is the only form that
 *     survives a mode segment sitting between the two.
 *
 * An empty or "/" base counts as case 2, not case 3: "" + "/create" is "/create"
 * at the site root, and "/" + "/create" is "//create", which a browser reads as
 * a protocol-relative URL and follows off-site.
 */
export function useListingCreateTarget(): string {
  const declared = useContext(ListingRouteBaseContext);
  const segment = declared?.createSegment ?? "create";
  const base = declared?.base;
  if (!base || base === "/") return segment;
  return `${base.replace(/\/+$/, "")}/${segment}`;
}

/**
 * Resolve a RECORD target for a listing that sits under a mode segment.
 *
 * Same problem as `useListingCreateTarget`, one route over: a listing mounted
 * at `path="list"` whose record page is a SIBLING of the mode group cannot
 * reach it with a bare relative navigate. React Router v6 resolves relative
 * against the matched ROUTE, so `navigate(String(id))` from that leaf produces
 * `<moduleBase>/list/<id>` — a path with no route, which falls through to the
 * module's catch-all and (where that catch-all redirects to the remembered
 * mode, as Bookings' does) bounces silently back to the list. Measured in
 * `src/bookings/__tests__/bookingsRecordNav.test.tsx`.
 *
 * `../<id>` also works and is what CompanyDataGrid used, but it is a hop count
 * — it is correct only while the listing sits exactly one segment below the
 * record route, so adding a segment breaks it silently. The declared base
 * survives that, and is the form this file already asks consumers to use.
 *
 * Falls back to a bare relative segment when no base is declared, so a
 * base-mounted listing keeps the behaviour it already had.
 */
export function useListingRecordTarget(): (id: string | number) => string {
  const declared = useContext(ListingRouteBaseContext);
  const base = declared?.base;
  return useCallback(
    (id: string | number) => {
      const segment = String(id);
      if (!base || base === "/") return segment;
      return `${base.replace(/\/+$/, "")}/${segment}`;
    },
    [base]
  );
}
